import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { NavigationEnd, Router } from "@angular/router";
import {
  concat,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
} from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
} from "rxjs/operators";
import { Bank } from "../../model/domain/Bank";
import { Expenses } from "../../model/domain/Expenses";
import { Favored } from "../../model/domain/Favored";
import { AuthService } from "../../services/auth.service";
import { BankService } from "../../services/bank.service";
import { DomainService } from "../../services/domain.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { Roles } from "../auth/auth.role";
import { SettlementType } from "./../../model/domain/Entity";

@Component({
  selector: "app-favored",
  templateUrl: "./favored.component.html",
  styleUrls: ["./favored.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class FavoredComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private service: TicketService,
    private router: Router,
    public validator: ValidatorService,
    private authService: AuthService,
    private bankService: BankService,
    private domainService: DomainService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        if (!this.isValidating) {
          this.resetForm();
        }
      }
    });
  }

  @ViewChild("thisForm", { static: true }) favoredForm: NgForm;

  @Input() favoredModel: Favored = new Favored();
  @Input() canEdit = false;
  @Input() showHeader;
  @Input() canValidate = false;
  @Input() model: Expenses;

  @Output() emitterSelectedFavored: EventEmitter<any> = new EventEmitter();
  @Output() emitterSelectedFavoredAccount: EventEmitter<any> =
    new EventEmitter();

  banksList: Bank[] = null;

  isValidating = false;
  formEnabled = true;
  navigationSubscription: Subscription;

  favoredList: Observable<Favored[]>;
  favoredSearchResult: Favored[] = null;
  favoredSearchTerm = new Subject<string>();

  favoredByName: Observable<Favored[]>;
  favoredByDocument: Observable<Favored[]>;
  favoredSearchNameTerm = new Subject<string>();
  favoredSearchDocumentTerm = new Subject<string>();

  selectedFavored: Favored = new Favored();
  favoredAccountList: Favored[] = null;
  selectedFavoredAccount: Favored = null;

  isNewFavored: boolean;

  ngOnInit() {
    this.getBanksList();
    this.searchFavored();
  }

  ngOnChanges() {
    //this.setEnable(false);
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  getBanksList() {
    this.domainService.ListBanks().subscribe((b) => {
      this.banksList = b;
    });
  }

  edit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateExpensesTicket)
    );
  }

  getErrors(): string[] {
    const validationErrors = [];
    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.favoredModel[k])) {
        validationErrors.push(k);
      }
    });

    if (this.favoredForm.invalid) {
      validationErrors.push("form is invalid");
    }

    return validationErrors;
  }

  searchFavored() {
    this.favoredByName = concat(
      of([]),
      this.favoredSearchNameTerm.pipe(
        filter((term) => term?.length >= 3),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((name) =>
          this.service.getFavored(name, null).pipe(catchError(() => of([])))
        )
      )
    );

    this.favoredByDocument = concat(
      of([]),
      this.favoredSearchDocumentTerm.pipe(
        filter((term) => term?.length >= 3),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((document) =>
          this.service.getFavored(null, document).pipe(catchError(() => of([])))
        )
      )
    );
  }

  fillComponentWhenFavoredSelected() {
    this.favoredModel.name = "";
    this.favoredModel.document = "";
    this.favoredModel.bank = "";
    this.favoredModel.branch = "";
    this.favoredModel.account = "";

    this.selectedFavoredAccount = null;

    if (this.selectedFavored) {
      if (this.selectedFavored.document.length == 14) {
        this.selectedFavored.document = this.validator.formatDocument(
          this.selectedFavored.document
        );
      } else {
        this.selectedFavored.document = this.validator.formatPhysicalDocument(
          this.selectedFavored.document
        );
      }

      this.favoredModel.name = this.selectedFavored.name;
      this.favoredModel.document = this.selectedFavored.document;
      this.favoredModel.bank = this.selectedFavored.bank;
      this.favoredModel.branch = this.selectedFavored.branch;
      this.favoredModel.account = this.selectedFavored.account;
    }
  }

  public fillFavoredData(data: any, favoredModel: Favored): any {
    //enviando campos do component para a model
    if (favoredModel.name) data.favoredName = favoredModel.name;

    data.isNewFavored = favoredModel.isNewFavored;

    if (favoredModel.document != null) {
      data.favoredDocument = this.validator.sanitize(favoredModel.document);
    }

    if (favoredModel.bank && favoredModel.branch && favoredModel.account) {
      data.favoredBank = favoredModel.bank;
      data.favoredBranch = favoredModel.branch;
      data.favoredAccount = favoredModel.account;
    }

    return data;
  }

  public fillComponentFieldsLoad<T>(model: any) {
    //carregando dados da model nos campos do component
    let data: any;
    data = Object.assign({}, model);

    try {
      this.favoredModel.name = this.selectedFavored.name = data.favoredName;
      this.favoredModel.document = this.selectedFavored.document =
        data.favoredDocument;
      this.favoredModel.isNewFavored = this.selectedFavored.isNewFavored =
        data.isNewFavored;
      this.favoredModel.bank = this.selectedFavored.bank = data.favoredBank;
      this.favoredModel.branch = this.selectedFavored.branch =
        data.favoredBranch;
      this.favoredModel.account = this.selectedFavored.account =
        data.favoredAccount;
      this.bankService.sendBank(this.selectedFavored.bank);
    } catch (error) {
      return throwError(error);
    }
  }

  coalesce(value: any) {
    return value || "";
  }

  resetForm() {
    this.selectedFavored = new Favored();
    this.selectedFavoredAccount = null;
    this.favoredAccountList = null;
    this.favoredForm.resetForm();

    this.setEnable(true);
  }

  setEnable(enabled: boolean = false) {
    this.formEnabled = enabled;
    setTimeout(() => {
      if (enabled) {
        Object.values(this.favoredForm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit) {
        Object.values(this.favoredForm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  hasError(prop: string): boolean {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.favoredModel[prop]);
  }

  validators = {
    document: (value) =>
      this.model.settlementTypeId == SettlementType.TED
        ? this.validator.validateCPFCNPJ(value)
        : true,
    name: (value) =>
      this.model.settlementTypeId == SettlementType.TED
        ? this.coalesce(value).length > 0
        : true,
    bank: (value) =>
      this.model.settlementTypeId == SettlementType.TED
        ? this.coalesce(value).length > 0
        : true,
    branch: (value) =>
      this.model.settlementTypeId == SettlementType.TED
        ? this.coalesce(value).length > 0
        : true,
    account: (value) =>
      this.model.settlementTypeId == SettlementType.TED
        ? this.coalesce(value).length > 0
        : true,
  };
}
